/** Pure geometry helpers over the model — no three.js, no rendering. */

import { ALP_LENGTHS, CFG, MM, MODS } from "../data/haki.ts";
import type { Beam, Bracket, Deck, DeckCorners, Point, ScaffoldModel } from "./types.ts";

/** Coordinate key for the leg map. Millimetre precision — coordinates must coincide exactly. */
export function kXZ(x: number, z: number): string {
  return `${x.toFixed(3)},${z.toFixed(3)}`;
}

export function dist(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

export function near(a: Point, b: Point, tolerance = 0.02): boolean {
  return Math.abs(a.x - b.x) < tolerance && Math.abs(a.z - b.z) < tolerance;
}

/** Order-independent identity for a beam / deck edge at one level. */
export function beamKey(a: Point, b: Point, p: number): string {
  return `${[kXZ(a.x, a.z), kXZ(b.x, b.z)].sort().join("|")}|${p}`;
}

export const edgeKey = beamKey;

export function nearestOf(list: readonly number[], value: number): number {
  return list.reduce((best, candidate) =>
    Math.abs(candidate - value) < Math.abs(best - value) ? candidate : best,
  );
}

export function nearestMod(value: number): number {
  return nearestOf(MODS, value);
}

export function deckKey(deck: Deck): string {
  return `${deck.c
    .map((c) => kXZ(c.x, c.z))
    .sort()
    .join("|")}|${deck.p}`;
}

export function yawOf(dx: number, dz: number): number {
  return Math.atan2(dx, dz);
}

/** A bay's local frame: X runs a→b, Z runs a→c. `sgn` corrects mirroring. */
export type BayFrame = Readonly<{
  a: Point;
  /** Length along a→b, metres. */
  L: number;
  /** Width along a→c, metres. */
  W: number;
  nx: number;
  nz: number;
  sgn: number;
  rotY: number;
  toWorld: (x: number, z: number) => Point;
  toLocal: (p: Point) => Point;
}>;

export function bayFrame(c: DeckCorners): BayFrame {
  const [a, b, cc] = c;
  const ux = b.x - a.x;
  const uz = b.z - a.z;
  const L = Math.hypot(ux, uz);
  const nx = ux / L;
  const nz = uz / L;
  const W = dist(a, cc);
  const sgn = (cc.x - a.x) * -nz + (cc.z - a.z) * nx > 0 ? 1 : -1;

  return {
    a,
    L,
    W,
    nx,
    nz,
    sgn,
    rotY: yawOf(nx, nz) - Math.PI / 2,
    toWorld: (x, z) => ({ x: a.x + nx * x + -nz * sgn * z, z: a.z + nz * x + nx * sgn * z }),
    toLocal: (p) => ({
      x: (p.x - a.x) * nx + (p.z - a.z) * nz,
      z: ((p.x - a.x) * -nz + (p.z - a.z) * nx) * sgn,
    }),
  };
}

export type EdgeInfo = Readonly<{
  /** True when the nearest edge runs along a→c rather than a→b. */
  alongAC: boolean;
  end: 0 | 1;
  side: 0 | 1;
  local: Point;
  frame: BayFrame;
}>;

/** Which of the four edges is nearest point `p`. */
export function nearestEdgeInfo(c: DeckCorners, p: Point): EdgeInfo {
  const frame = bayFrame(c);
  const local = frame.toLocal(p);
  const edges: [number, string][] = [
    [local.z, "ab"],
    [frame.W - local.z, "cd"],
    [local.x, "ac"],
    [frame.L - local.x, "bd"],
  ];
  edges.sort((u, v) => u[0] - v[0]);
  const nearestName = edges[0]?.[1];

  return {
    alongAC: nearestName === "ac" || nearestName === "bd",
    end: local.x > frame.L / 2 ? 1 : 0,
    side: local.z > frame.W / 2 ? 1 : 0,
    local,
    frame,
  };
}

/** Swap the plank direction of a bay's corners. */
export function orientCorners(c: DeckCorners, alongAC: boolean): DeckCorners {
  const [a, b, cc, d] = c;
  return alongAC ? [a, cc, b, d] : [a, b, cc, d];
}

export function findBeam(model: ScaffoldModel, a: Point, b: Point, p: number): Beam | undefined {
  const key = beamKey(a, b, p);
  return model.beams.find((beam) => beamKey(beam.a, beam.b, beam.p) === key);
}

/**
 * An SK bracket's arm as a beam-shaped edge. The SKD's arm is a real beam,
 * so only the SK contributes; the arm hooks both cups like an ERB does.
 */
export function bracketArm(bracket: Bracket): Beam | null {
  if (bracket.kind !== "sk") {
    return null;
  }

  return { a: bracket.a, b: bracket.b, p: bracket.p, kind: "erb", len: bracket.len };
}

/**
 * Every edge a deck can rest on or a guardrail can hook onto: the beams plus
 * the SK bracket arms, which carry planks exactly as a transom does.
 */
export function bayEdges(model: ScaffoldModel): Beam[] {
  const arms: Beam[] = [];
  for (const bracket of model.brackets) {
    const arm = bracketArm(bracket);
    if (arm) {
      arms.push(arm);
    }
  }

  return [...model.beams, ...arms];
}

export function findEdge(model: ScaffoldModel, a: Point, b: Point, p: number): Beam | undefined {
  const key = beamKey(a, b, p);
  return bayEdges(model).find((edge) => beamKey(edge.a, edge.b, edge.p) === key);
}

/** The bracket whose stub stands at `b`, carrying the cup group at level `p`. */
export function bracketUnder(model: ScaffoldModel, b: Point, p: number): Bracket | undefined {
  return model.brackets.find((bracket) => bracket.p === p && near(bracket.b, b));
}

export function legAt(model: ScaffoldModel, x: number, z: number) {
  return model.legs.get(kXZ(x, z));
}

export type Bay = Readonly<{
  c: DeckCorners;
  p: number;
  len: number;
  wid: number;
  key: string;
}>;

/** The shared corner of two beams meeting at one end, and the far corner of the second. */
function cornerPair(ab: Beam, ac: Beam): { a: Point; c: Point } | null {
  if (near(ac.a, ab.a)) {
    return { a: ab.a, c: ac.b };
  }
  if (near(ac.b, ab.a)) {
    return { a: ab.a, c: ac.a };
  }

  return null;
}

/** Does a closed rectangle exist on these two perpendicular beams? */
function closedBay(model: ScaffoldModel, ab: Beam, ac: Beam): Bay | null {
  const pair = cornerPair(ab, ac);
  if (!pair) {
    return null;
  }

  const { a: A, c: C } = pair;
  const length = Math.hypot(ab.b.x - ab.a.x, ab.b.z - ab.a.z);
  const u = { x: (ab.b.x - ab.a.x) / length, z: (ab.b.z - ab.a.z) / length };
  const v = { x: C.x - A.x, z: C.z - A.z };
  if (Math.abs(u.x * v.x + u.z * v.z) > 0.03) {
    return null;
  }

  const D: Point = { x: ab.b.x + v.x, z: ab.b.z + v.z };
  const corners = [A, ab.b, C, D];
  if (corners.some((c) => !legAt(model, c.x, c.z))) {
    return null;
  }
  if (!findEdge(model, ab.b, D, ab.p) || !findEdge(model, C, D, ab.p)) {
    return null;
  }

  let b = ab.b;
  let c = C;
  const abIsLB = ab.kind === "lb";
  const acIsLB = ac.kind === "lb";
  if ((!abIsLB && acIsLB) || (abIsLB === acIsLB && dist(A, C) > dist(A, ab.b))) {
    b = C;
    c = ab.b;
  }

  return {
    c: [A, b, c, { x: b.x + (c.x - A.x), z: b.z + (c.z - A.z) }],
    p: ab.p,
    len: nearestOf(ALP_LENGTHS, dist(A, b) * 1000),
    wid: Math.round(dist(A, c) * 1000),
    key: `${corners
      .map((n) => kXZ(n.x, n.z))
      .sort()
      .join("|")}|${ab.p}`,
  };
}

/**
 * Closed rectangular bays reachable from the beam graph: four legs joined by
 * four edges at one level — beams, or an SK bracket's arm. The plank direction
 * follows the LB beams, falling back to the longest side when both edges are
 * the same kind.
 */
export function findBays(model: ScaffoldModel): Map<string, Bay> {
  const found = new Map<string, Bay>();
  const edges = bayEdges(model);

  for (const ab of edges) {
    for (const ac of edges) {
      if (ac === ab || ac.p !== ab.p) {
        continue;
      }

      const bay = closedBay(model, ab, ac);
      if (bay && !found.has(bay.key)) {
        found.set(bay.key, bay);
      }
    }
  }

  return found;
}

/** A pair of parallel edges a set distance apart — a UTV latches between transoms, a board rests on beams. */
export type StairSpan = Readonly<{
  lo: Beam;
  hi: Beam;
  p: number;
  len: number;
  u: Point;
  dir: Point;
  /** Lateral overlap the 600 mm flight can slide within. */
  o0: number;
  o1: number;
}>;

type SpanQuery = Readonly<{
  edges: readonly Beam[];
  /** Node levels the second edge sits above the first — 0 for a board, 4 for a UTV. */
  levelsAbove: number;
  /** Centre distances that count, millimetres. */
  sizes: readonly number[];
  minOverlap: number;
  /** Both directions of a pair when true; one when the order does not matter. */
  directed: boolean;
}>;

/** The span between two parallel edges, or null when they are not parallel or too far off any size. */
function spanBetween(lo: Beam, hi: Beam, sizes: readonly number[], minOverlap: number): StairSpan | null {
  const l1 = Math.hypot(lo.b.x - lo.a.x, lo.b.z - lo.a.z);
  const u = { x: (lo.b.x - lo.a.x) / l1, z: (lo.b.z - lo.a.z) / l1 };
  const l2 = Math.hypot(hi.b.x - hi.a.x, hi.b.z - hi.a.z);
  const v = { x: hi.b.x - hi.a.x, z: hi.b.z - hi.a.z };
  if (Math.abs((u.x * v.x) / l2 + (u.z * v.z) / l2) < 0.98) {
    return null;
  }

  const n = { x: -u.z, z: u.x };
  const d = (hi.a.x - lo.a.x) * n.x + (hi.a.z - lo.a.z) * n.z;
  const size = sizes.find((candidate) => Math.abs(Math.abs(d) - candidate * MM) <= 0.06);
  if (size === undefined) {
    return null;
  }

  const proj = (p: Point) => (p.x - lo.a.x) * u.x + (p.z - lo.a.z) * u.z;
  const loEnd = proj(lo.b);
  const hiA = proj(hi.a);
  const hiB = proj(hi.b);
  const o0 = Math.max(Math.min(0, loEnd), Math.min(hiA, hiB));
  const o1 = Math.min(Math.max(0, loEnd), Math.max(hiA, hiB));
  if (o1 - o0 < minOverlap) {
    return null;
  }

  return { lo, hi, p: lo.p, len: size, u, dir: { x: n.x * Math.sign(d), z: n.z * Math.sign(d) }, o0, o1 };
}

/** Pairs of parallel edges whose perpendicular distance matches one of `sizes`, with room across for `minOverlap`. */
function parallelSpans(query: SpanQuery): StairSpan[] {
  const { edges, levelsAbove, sizes, minOverlap, directed } = query;
  const result: StairSpan[] = [];
  const seen = new Set<string>();

  for (const lo of edges) {
    for (const hi of edges) {
      if (hi === lo || hi.p !== lo.p + levelsAbove) {
        continue;
      }

      const span = spanBetween(lo, hi, sizes, minOverlap);
      if (!span) {
        continue;
      }

      const pair = [edgeKey(lo.a, lo.b, lo.p), edgeKey(hi.a, hi.b, hi.p)];
      const key = (directed ? pair : pair.sort()).join("~");
      if (!seen.has(key)) {
        seen.add(key);
        result.push(span);
      }
    }
  }

  return result;
}

/**
 * Every pair of parallel transoms with the upper one at `p+4` and `size` mm
 * apart, wide enough for the flight. Both ascend directions of a pair are
 * returned; the pointer picks which end becomes the bottom.
 */
export function findStairSpans(model: ScaffoldModel, size: number): StairSpan[] {
  return parallelSpans({
    edges: model.beams,
    levelsAbove: 4,
    sizes: [size],
    minOverlap: CFG.utv.width * MM - 0.02,
    directed: true,
  });
}

/**
 * Every pair of parallel edges at one level an ALP length apart with room for
 * at least one board of `widthMm` across — where a single board can be laid.
 */
export function findBoardSpans(model: ScaffoldModel, widthMm: number): StairSpan[] {
  return parallelSpans({
    edges: bayEdges(model),
    levelsAbove: 0,
    sizes: ALP_LENGTHS,
    minOverlap: widthMm * MM + 0.09,
    directed: false,
  });
}

/**
 * Uncovered runs of [x0, x1] sampled with `covered(x)` — used to split planks
 * and rails around stair openings.
 */
export function openRuns(x0: number, x1: number, covered: (x: number) => boolean): [number, number][] {
  const steps = Math.max(10, Math.round((x1 - x0) / 0.1));
  const runs: [number, number][] = [];
  let current: [number, number] | null = null;

  for (let i = 0; i <= steps; i++) {
    const x = x0 + ((x1 - x0) * i) / steps;
    if (covered(x)) {
      if (current) {
        runs.push(current);
        current = null;
      }
      continue;
    }

    if (current) {
      current[1] = x;
    } else {
      current = [x, x];
    }
  }
  if (current) {
    runs.push(current);
  }

  return runs;
}
